import React from 'react';
import {
  Typography,
  Button,
  Grid,
  IconButton,
  Paper,
  Box,
  Divider,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import UpdateIcon from '@mui/icons-material/Update';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { convertTime, safeParseFloat, formatCurrencyDisplay } from '../../utils';
import ScheduleSelector from './ScheduleSelector';
import { EventDetail, PricingDetail, PricingDiscountTypes } from '../../types';
import SelectChildModal from '../children/SelectChildModal';
import LoginSignupModal from '../elements/LoginSignupModal';
import { PRICING_STATUS_ACTIVE } from '../../constants';
import { calculateDiscountedPrice } from '../../utils';

interface EventPricingTiersBoxesProps {
  eventDetails: EventDetail;
  user: any;
  className: string;
  isItemInCart: (pricingId: number, childId: number) => boolean;
  /** True when any pricing tier for this event is in cart for the given child. */
  isEventInCart: (eventId: number, childId: number) => boolean;
  handleAddToCartClick: (
    pricingId: number,
    isSelectScheduleAllowed: boolean,
    maxScheduleDays: number,
    selectedDaysArray: string[],
    handleAccordion: boolean,
  ) => void;
  handleRemoveItemFromCart: (pricingId: number) => void;
  handleAddToWaitlist: (pricingId: number) => void;
  expandedAccordions: Record<number, boolean>;
  setExpandedAccordions: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  /** True while cart operations for this event's pricing tiers are in progress. */
  isPricingUpdating: boolean;
}

const EventPricingTiersBoxes: React.FC<EventPricingTiersBoxesProps> = ({
  eventDetails,
  user,
  className,
  isItemInCart,
  isEventInCart,
  handleAddToCartClick,
  handleRemoveItemFromCart,
  handleAddToWaitlist,
  expandedAccordions,
  setExpandedAccordions,
  isPricingUpdating,
}) => {
  const [showChildModal, setShowChildModal] = React.useState(false);
  const [showLoginSignupModal, setShowLoginSignupModal] = React.useState(false);

  // Helper function to calculate discount badge text
  const getDiscountBadgeText = (pricing: PricingDetail): string => {
    if (pricing.discountType === PricingDiscountTypes.PERCENTAGE) {
      return `${Math.round(Number(pricing.discountAmount))}% OFF`;
    } else if (pricing.discountType === PricingDiscountTypes.FIXED) {
      return `$${formatCurrencyDisplay(pricing.discountAmount)} OFF`;
    }
    return '';
  };

  const enabledTiers = eventDetails.pricing.filter((p: PricingDetail) => p.status === PRICING_STATUS_ACTIVE);

  return (
    <>
      <Typography variant="h6" gutterBottom pt={2} pb={2}>
        Pricing Tiers
      </Typography>
      {enabledTiers.length === 0 && eventDetails.pricing.length > 0 && (
        <Box sx={{ py: 2, px: 2, backgroundColor: '#FFF3E0', borderRadius: 1, border: '1px solid #FF9800' }}>
          <Typography variant="body1" sx={{ color: '#E65100', fontWeight: 500 }}>
            Pricing is not currently available. There may be a scheduled gap between pricing tiers. Please check back
            later.
          </Typography>
        </Box>
      )}
      <Grid container spacing={3}>
        {enabledTiers.map((pricing: PricingDetail) => (
          <Grid item xs={12} md={4} key={pricing.id} sx={{ display: 'flex' }}>
            <Paper
              elevation={3}
              sx={{
                pt: 2,
                pb: 2,
                borderBottom: '0 !important',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Fixed Content Section - Title, Time, Price */}
              <Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={12} textAlign="center">
                    <Typography variant="h6" className="fw-700 ff-inter">
                      {pricing.description}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} textAlign="center">
                    <Typography variant="body2" className="ff-inter typography-time">
                      {convertTime(pricing.startTime)} - {convertTime(pricing.endTime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={12} textAlign="center">
                    <Typography
                      variant="body2"
                      className="typography-price"
                      color="#297AB0"
                      sx={{ fontSize: '0.375rem', fontWeight: 500 }}
                    >
                      Event Price: ${pricing.chargePerPeriod}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Options Section */}
              <Box sx={{ mt: 2, px: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Pay in Full Option */}
                {pricing.discountType !== 0 &&
                  (() => {
                    const discountedPrice = safeParseFloat(
                      calculateDiscountedPrice(
                        pricing.chargePerPeriod,
                        pricing.discountType,
                        pricing.discountAmount,
                        undefined,
                      ),
                    );
                    const fullPrice = safeParseFloat(pricing.chargePerPeriod);
                    const savings = fullPrice - discountedPrice;

                    return (
                      <Box
                        sx={{
                          backgroundColor: '#E8F5E9',
                          border: '2px solid #4CAF50',
                          borderRadius: '8px',
                          p: 1.5,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#2E7D32',
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          PAY IN FULL TODAY
                        </Typography>
                        <Chip
                          label={getDiscountBadgeText(pricing)}
                          size="small"
                          sx={{
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            height: '24px',
                          }}
                        />
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: '#2E7D32',
                            fontSize: '1.75rem',
                          }}
                        >
                          ${formatCurrencyDisplay(discountedPrice)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#2E7D32',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                          }}
                        >
                          Save ${formatCurrencyDisplay(savings)}
                        </Typography>
                      </Box>
                    );
                  })()}

                {/* OR Divider */}
                {pricing.discountType !== 0 &&
                  !isNaN(Number(pricing.depositAmount)) &&
                  Number(pricing.depositAmount) > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 0.5 }}>
                      <Divider sx={{ flex: 1 }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontWeight: 600,
                          px: 1,
                        }}
                      >
                        OR
                      </Typography>
                      <Divider sx={{ flex: 1 }} />
                    </Box>
                  )}

                {/* Deposit Option */}
                {!isNaN(Number(pricing.depositAmount)) &&
                  Number(pricing.depositAmount) > 0 &&
                  (() => {
                    const depositAmount = safeParseFloat(pricing.depositAmount);
                    const fullPrice = safeParseFloat(pricing.chargePerPeriod);
                    const balanceDue = fullPrice - depositAmount;

                    return (
                      <Box
                        sx={{
                          backgroundColor: '#FFF3E0',
                          border: '2px solid #FF9800',
                          borderRadius: '8px',
                          p: 1.5,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: '#E65100',
                            fontSize: '0.875rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          DEPOSIT OPTION
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: '#FF9800',
                            fontSize: '1.75rem',
                          }}
                        >
                          ${formatCurrencyDisplay(depositAmount)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#E65100',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          }}
                        >
                          Balance: ${formatCurrencyDisplay(balanceDue)} due later
                        </Typography>
                      </Box>
                    );
                  })()}
              </Box>

              {/* Action Button Section */}
              <Box sx={{ mt: 'auto', pt: 2 }}>
                <Grid item xs={12} sm={12} textAlign="center">
                  {!user && (
                    <Button onClick={() => setShowLoginSignupModal(true)} disabled={isPricingUpdating}>
                      {isPricingUpdating ? <CircularProgress size={18} color="inherit" /> : 'Login or Sign up'}
                    </Button>
                  )}
                  {user && (!user.children || user.children.length === 0) && (
                    <Button onClick={() => setShowChildModal(true)} disabled={isPricingUpdating}>
                      {isPricingUpdating ? <CircularProgress size={18} color="inherit" /> : 'Add Child'}
                    </Button>
                  )}
                  {user && user.children && user.children.length > 0 && !user.selectedChild && (
                    <Button
                      onClick={() => setShowChildModal(true)}
                      sx={{ mb: 1, minWidth: 170 }}
                      disabled={isPricingUpdating}
                    >
                      {isPricingUpdating ? 'LOADING' : 'Select Child'}
                    </Button>
                  )}
                  {user && user.children && user.children.length > 0 && user.selectedChild && (
                    <Box sx={{ mb: 1.5 }}>
                      <Button
                        onClick={() => setShowChildModal(true)}
                        variant="outlined"
                        size="small"
                        sx={{ fontSize: '0.8rem', textTransform: 'none', minWidth: 170 }}
                        disabled={isPricingUpdating}
                      >
                        {isPricingUpdating ? (
                          'LOADING'
                        ) : (
                          <>
                            {user.selectedChild.fname} {user.selectedChild.lname} (Change)
                          </>
                        )}
                      </Button>
                    </Box>
                  )}
                  {user && user.selectedChild && className === 'registered' && (
                    <Box
                      sx={{
                        mx: 2,
                        px: 2,
                        py: 1.5,
                        backgroundColor: '#E8F5E9',
                        borderRadius: '8px',
                        border: '2px solid #4CAF50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                      }}
                    >
                      <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: '20px' }} />
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#2E7D32',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                        }}
                      >
                        {user.selectedChild.fname} {user.selectedChild.lname} is registered for this event
                      </Typography>
                    </Box>
                  )}
                  {user &&
                    user.selectedChild &&
                    className === 'open' &&
                    (eventDetails.totalOccupiedSeats ?? 0) < eventDetails.capacity &&
                    (() => {
                      const inCart = isItemInCart(pricing.id, user.selectedChild.id);
                      const eventInCart = isEventInCart(eventDetails.id, user.selectedChild.id);
                      const disabledForThisTier = eventInCart && !inCart;
                      const scheduleExpanded = expandedAccordions[pricing.id];

                      if (inCart) {
                        return (
                          <Box sx={{ mb: 1.5 }}>
                            <Button
                              className="added-to-cart cursor-pointer btn-add-cart"
                              disabled={isPricingUpdating}
                              sx={{ minWidth: 170 }}
                              startIcon={<AddShoppingCartIcon />}
                            >
                              {isPricingUpdating ? 'LOADING' : 'Added to Cart'}
                              <Tooltip
                                title="Remove item from cart"
                                className="pt-0 btn-cart"
                                onClick={() => handleRemoveItemFromCart(pricing.id)}
                              >
                                <IconButton className="ptf-0 ptb-0">
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </Button>
                          </Box>
                        );
                      }

                      if (scheduleExpanded) {
                        return null;
                      }

                      return (
                        <Button
                          onClick={() => setExpandedAccordions((prev) => ({ ...prev, [pricing.id]: true }))}
                          disabled={disabledForThisTier || isPricingUpdating}
                          className={disabledForThisTier ? 'btn-add-cart disabled' : 'cursor-pointer btn-add-cart'}
                          startIcon={<AddShoppingCartIcon />}
                          sx={{
                            ...(disabledForThisTier
                              ? {
                                  backgroundColor: '#E0E0E0 !important',
                                  color: '#9E9E9E !important',
                                  cursor: 'not-allowed',
                                  '&:hover': { backgroundColor: '#E0E0E0 !important' },
                                  '& .MuiSvgIcon-root': { color: '#9E9E9E !important' },
                                }
                              : {}),
                            minWidth: 170,
                          }}
                        >
                          {isPricingUpdating ? 'LOADING' : 'Add to Cart'}
                        </Button>
                      );
                    })()}
                  {!!user &&
                    !!eventDetails.isWaitlistEnabled &&
                    eventDetails.occupiedSeats >= eventDetails.capacity && (
                      <Box sx={{ mt: 1 }}>
                        <Button
                          onClick={() => handleAddToWaitlist(pricing.id)}
                          className="cursor-pointer btn-add-cart"
                          startIcon={<UpdateIcon />}
                          disabled={isPricingUpdating}
                        >
                          {isPricingUpdating ? <CircularProgress size={18} color="inherit" /> : 'Add to Waitlist'}
                        </Button>
                      </Box>
                    )}
                </Grid>
              </Box>

              {/* Schedule Selector - shown after Add to Cart opens the panel for both tier types */}
              {user?.selectedChild &&
                className === 'open' &&
                (eventDetails.totalOccupiedSeats ?? 0) < eventDetails.capacity &&
                !isItemInCart(pricing.id, user.selectedChild.id) &&
                expandedAccordions[pricing.id] && (
                  <ScheduleSelector
                    pricing={pricing}
                    expanded={true}
                    setExpanded={(isExpanded) =>
                      setExpandedAccordions((prev) => ({ ...prev, [pricing.id]: isExpanded }))
                    }
                    onAddToCart={(selectedDays) =>
                      handleAddToCartClick(
                        pricing.id,
                        pricing.isSelectScheduleAllowed,
                        pricing.maxScheduleDays,
                        selectedDays,
                        false,
                      )
                    }
                    isPricingUpdating={isPricingUpdating}
                  />
                )}
            </Paper>
          </Grid>
        ))}
      </Grid>
      <SelectChildModal open={showChildModal} onClose={() => setShowChildModal(false)} />
      <LoginSignupModal open={showLoginSignupModal} onClose={() => setShowLoginSignupModal(false)} />
    </>
  );
};

export default EventPricingTiersBoxes;
